import { t } from "@lingui/macro";
import React from "react";

import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { Theme } from "@whoosh/design-system/src/theme/restyle/theme";

import {
  CheckIcon,
  PlaceIcon,
} from "../../../../../../../../components/icons_v2/general";

type TimeSlotHeaderProps = {
  time?: string;
  badgeText?: string;
  ctaText?: string;
  badgeBackground?: keyof Theme["colors"];
  badgeTextColor?: keyof Theme["colors"];
  headingTextColor?: keyof Theme["colors"];
  shouldShowCheckbox?: boolean;
  checkboxIsChecked?: boolean;
  location?: string;
};

export const TimeSlotHeader = ({
  time,
  badgeText,
  ctaText,
  badgeBackground,
  badgeTextColor,
  headingTextColor,
  shouldShowCheckbox,
  checkboxIsChecked,
  location,
}: TimeSlotHeaderProps) => {
  return (
    <VStack gap={2}>
      <HStack alignItems="center" justifyContent="space-between">
        <VStack>
          {location ? (
            <HStack alignItems="center" gap={1}>
              <Text
                maxFontSizeMultiplier={2}
                variant="mainTiny"
                color="text-secondary"
              >
                {location}
              </Text>
              <PlaceIcon color="icon-disabled" />
            </HStack>
          ) : null}
          <Text
            maxFontSizeMultiplier={2}
            variant="mainBaseSemiBold"
            color={headingTextColor}
          >
            {time ?? ""}
          </Text>
        </VStack>
        <HStack>
          {badgeText ? (
            <Box
              backgroundColor={badgeBackground}
              px={2}
              py={1}
              borderRadius="2"
            >
              <Text
                maxFontSizeMultiplier={2}
                color={badgeTextColor}
                variant="mainTiny"
              >
                {badgeText}
              </Text>
            </Box>
          ) : null}
          {!!ctaText && !shouldShowCheckbox ? (
            <HStack ml={4} alignItems="center">
              <Text
                maxFontSizeMultiplier={2}
                variant="mainBaseSemiBold"
                color={headingTextColor}
              >
                {ctaText}
              </Text>
              <Box
                ml={2}
                style={{
                  width: 0,
                  height: 0,
                  backgroundColor: "transparent",
                  borderStyle: "solid",
                  borderLeftWidth: 4,
                  borderRightWidth: 4,
                  borderBottomWidth: 4,
                  borderLeftColor: "transparent",
                  borderRightColor: "transparent",
                  transform: [{ rotate: "90deg" }],
                }}
                borderBottomColor={headingTextColor}
              />
            </HStack>
          ) : null}
          {!!shouldShowCheckbox ? (
            <HStack ml={4} alignItems="center" gap={3}>
              <FakeCheckbox isChecked={!!checkboxIsChecked} />
              <Text
                maxFontSizeMultiplier={2}
                variant="mainBaseMedium"
                color={headingTextColor}
              >
                {t`Select`}
              </Text>
            </HStack>
          ) : null}
        </HStack>
      </HStack>
    </VStack>
  );
};

type FakeCheckboxProps = {
  isChecked: boolean;
};

// This is needed for performance reasons. Using the NativeBase Checkbox component is too slow.
const FakeCheckbox = ({ isChecked }: FakeCheckboxProps) => {
  return (
    <Box
      height={20}
      width={20}
      bg={isChecked ? "surface-main-primary-neutral" : undefined}
      borderColor={isChecked ? "surface-main-primary-neutral" : "grayscale-700"}
      borderWidth={1.5}
      borderRadius="1"
      alignItems="center"
      justifyContent="center"
    >
      {isChecked ? (
        <CheckIcon height="2px" width="2px" color="icon-inverse" />
      ) : null}
    </Box>
  );
};
